import React, { useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface HiringSectionProps {
  category: string;
  values: {
    startingHeadcount: number;
    incrementalHires: number;
    endingHeadcount: number;
    backfillPercentage: number;
    backfillHires: number;
    plannedGrossHires: number;
  };
  onChange: (field: string, value: number) => void;
}

export const HiringSection = ({
  category,
  values,
  onChange,
}: HiringSectionProps) => {
  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow any numeric input including empty string and zero
    if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
      const numericValue = value === '' ? 0 : Number(value);
      onChange(field, numericValue);
    }
  };

  // Calculate backfill hires, ending headcount and planned gross hires
  useEffect(() => {
    // Guard against undefined values
    if (
      values.startingHeadcount === undefined ||
      values.backfillPercentage === undefined ||
      values.incrementalHires === undefined
    ) {
      return;
    }

    // Calculate new values
    const endingHeadcount = Number(values.startingHeadcount) + Number(values.incrementalHires);
    const backfillHires = Math.round(
      (Number(values.startingHeadcount) * Number(values.backfillPercentage)) / 100
    );
    const plannedGrossHires = Number(values.incrementalHires) + backfillHires;

    // Only update if values have actually changed
    const hasChanges = 
      backfillHires !== values.backfillHires ||
      plannedGrossHires !== values.plannedGrossHires ||
      endingHeadcount !== values.endingHeadcount;

    if (hasChanges) {
      onChange('endingHeadcount', endingHeadcount);
      onChange('backfillHires', backfillHires);
      onChange('plannedGrossHires', plannedGrossHires);
    }
  }, [
    category,
    values.startingHeadcount,
    values.incrementalHires,
    values.backfillPercentage,
    onChange
  ]);

  // Format value for display - return empty string for 0 only in display
  const formatDisplayValue = (value: number) => value === 0 && !String(value).length ? '' : String(value);

  return (
    <div className="grid grid-cols-7 gap-4 items-center mb-4">
      <div>
        <Label className="text-sm font-medium whitespace-nowrap">{category}</Label>
      </div>
      <div>
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={formatDisplayValue(values.startingHeadcount)}
          onChange={handleChange('startingHeadcount')}
          className="w-full"
          placeholder="Starting"
        />
      </div>
      <div>
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={formatDisplayValue(values.incrementalHires)}
          onChange={handleChange('incrementalHires')}
          className="w-full"
          placeholder="Growth"
        />
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={formatDisplayValue(values.endingHeadcount)}
          className="w-full bg-gray-100"
          placeholder="Ending"
          readOnly
        />
        <span className="text-sm text-gray-500 whitespace-nowrap">
          {values.startingHeadcount === 0 ? 0 : Math.round(((values.endingHeadcount - values.startingHeadcount) / values.startingHeadcount) * 100)}%
        </span>
      </div>
      <div>
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={formatDisplayValue(values.backfillPercentage)}
          onChange={handleChange('backfillPercentage')}
          className="w-full"
          placeholder="Attrition %"
        />
      </div>
      <div>
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={formatDisplayValue(values.backfillHires)}
          className="w-full bg-gray-100"
          placeholder="Backfill"
          readOnly
        />
      </div>
      <div>
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={formatDisplayValue(values.plannedGrossHires)}
          className="w-full bg-gray-100"
          placeholder="Total"
          readOnly
        />
      </div>
    </div>
  );
};